import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import {
   DAY_CHOICES,
   ordinal,
   firstBillLabel,
} from './IspAccountForm';
import {
   getIspPlans,
   getIspAccountCredentials,
   type IspAccountRow,
} from '@/api/isp';

/**
 * Change a line without tearing it down.
 *
 * Ray, 2026-09-01: *"in accounts it should have upgrade plan or edit specially
 * the login."* Until now the only way to move somebody onto a faster plan was
 * to disconnect them and build a new account, which threw away their invoices
 * and payments to change one field.
 *
 * ─── ONLY PLANS THAT RUN ON THEIR ROUTER ───
 * 🔴 A plan enforces itself through a /ppp/profile living on ONE MikroTik. Move
 * somebody to a plan that router has never heard of and RouterOS silently falls
 * back to its default profile: they get whatever that allows, at the price of
 * the plan they were moved to, and no screen looks wrong. The server refuses it
 * too — this list just stops the operator meeting that refusal.
 *
 * ─── THE PASSWORD IS BLANK, NOT PRE-FILLED ───
 * Left empty means "leave it alone". Showing the current one invites an
 * accidental overwrite of credentials somebody has already written on a card
 * and given to a customer.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;

interface Props {
   account: IspAccountRow;
   customerName: string;
   currentPlanName: string;
   serverName: string;
   onCancel: () => void;
   onSave: (input: {
      plan?: string;
      pppoePassword?: string;
      dueDay?: number;
   }) => void;
   saving?: boolean;
}

const IspAccountEdit: React.FC<Props> = ({
   account,
   customerName,
   currentPlanName,
   serverName,
   onCancel,
   onSave,
   saving,
}) => {
   const { data: plans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });

   /**
    * The login they signed in with, for reading back down the phone.
    *
    * Ray, 2026-09-02: *"it should show the user and password so if forget it
    * will easy to get."* Hidden until asked for — an operator's screen sits on
    * a counter, and a password nobody requested does not need to be on it.
    */
   const { data: creds } = useQuery({
      queryKey: ['isp-account-credentials', account.id],
      queryFn: () => getIspAccountCredentials(account.id),
   });
   const [showPass, setShowPass] = useState(false);

   const [plan, setPlan] = useState(account.plan);
   const [dueDay, setDueDay] = useState(String(account.dueDay));
   const [password, setPassword] = useState('');
   const [message, setMessage] = useState('');

   const usable = plans.filter((p) => p.servers.includes(account.server));
   const chosen = plans.find((p) => p.id === plan);
   const movingPlan = plan !== account.plan;

   /** Copying can be blocked; saying so beats a button that does nothing. */
   const copy = (text: string) =>
      navigator.clipboard
         ?.writeText(text)
         .then(() => setMessage('Copied.'))
         .catch(() => setMessage('Could not copy — select it and copy by hand.'));

   const submit = () => {
      const day = Number(dueDay);
      if (!Number.isFinite(day) || day < 1 || day > 30) {
         setMessage('Bill day must be between 1 and 30.');
         return;
      }
      if (password && password.trim().length < 4) {
         setMessage('A PPPoE password needs at least 4 characters.');
         return;
      }
      setMessage('');
      onSave({
         ...(movingPlan ? { plan } : {}),
         ...(password.trim() ? { pppoePassword: password.trim() } : {}),
         dueDay: Math.trunc(day),
      });
   };

   return (
      <Wrap>
         <Who>
            {customerName} · <Mono>{account.pppoeUsername}</Mono> on {serverName}
         </Who>

         <Creds>
            <CredRow>
               <CredKey>PPPoE username</CredKey>
               <CredVal>
                  <Mono>{creds?.pppoeUsername ?? account.pppoeUsername}</Mono>
                  <CopyBtn
                     type="button"
                     onClick={() =>
                        copy(creds?.pppoeUsername ?? account.pppoeUsername)
                     }
                  >
                     Copy
                  </CopyBtn>
               </CredVal>
            </CredRow>
            <CredRow>
               <CredKey>PPPoE password</CredKey>
               <CredVal>
                  <Mono>
                     {!creds
                        ? '…'
                        : showPass
                          ? creds.pppoePassword || '(none set)'
                          : '••••••••'}
                  </Mono>
                  <CopyBtn type="button" onClick={() => setShowPass((v) => !v)}>
                     {showPass ? 'Hide' : 'Show'}
                  </CopyBtn>
                  {showPass && creds?.pppoePassword ? (
                     <CopyBtn
                        type="button"
                        onClick={() => copy(creds.pppoePassword)}
                     >
                        Copy
                     </CopyBtn>
                  ) : null}
               </CredVal>
            </CredRow>
            <Hint>
               What this customer&apos;s router signs in with. Read it back to
               them — changing it below is a different thing.
            </Hint>
         </Creds>

         <Field>
            <Lbl htmlFor="edit-plan">Plan</Lbl>
            <Select
               id="edit-plan"
               value={plan}
               onChange={(e) => setPlan(e.target.value)}
            >
               {/* The plan they are on stays listed even if it no longer runs
                   on this router — otherwise the dropdown silently proposes a
                   change nobody asked for. */}
               {!usable.some((p) => p.id === account.plan) ? (
                  <option value={account.plan}>{currentPlanName} (current)</option>
               ) : null}
               {usable.map((p) => (
                  <option key={p.id} value={p.id}>
                     {p.name} — {peso(p.price)}/month
                  </option>
               ))}
            </Select>
            <Hint>
               Only plans that run on {serverName}. Add this MikroTik to a plan
               under Internet plans to offer more here.
            </Hint>
         </Field>

         {movingPlan && account.status === 'suspended' ? (
            // Said before they press save, not after: an operator changing a
            // suspended customer's plan usually expects it to reconnect them.
            <Warn>
               They are switched off. Changing the plan does <strong>not</strong>{' '}
               put them back online — they stay off until you resume them.
            </Warn>
         ) : null}
         {movingPlan && account.status === 'active' ? (
            <Info>
               Saving writes <Mono>{chosen?.name}</Mono> to {serverName}{' '}
               immediately. Their speed changes on their next reconnect.
            </Info>
         ) : null}

         {/* 🔴 THE SAME QUESTION THE ADD FORM ASKS. Ray, 2026-09-04: *"the
             bills on should be replaced 'When do you collect from them?'
             instead of day on right? here in edit modal its not being replaced
             yet."* It was replaced in the Add form and missed here, so the
             same field asked a friendly question in one place and demanded a
             number from 1 to 30 in the other — and Edit is the one an operator
             opens far more often.

             The choices, the ordinals and the preview all come from the Add
             form, so the two cannot drift apart again. The preview says "Next
             bill" rather than "First bill": this line already exists and has
             probably been billed before. */}
         <Field>
            <Lbl htmlFor="edit-day">When do you collect from them?</Lbl>
            <Select
               id="edit-day"
               value={dueDay}
               onChange={(e) => setDueDay(e.target.value)}
            >
               {DAY_CHOICES.map((d) => (
                  <option key={d} value={String(d)}>
                     Every month on the {ordinal(d)}
                     {d === new Date().getDate() ? ' — today' : ''}
                  </option>
               ))}
            </Select>
            <Hint>
               Next bill: <strong>{firstBillLabel(Number(dueDay))}</strong>
            </Hint>
         </Field>

         <Field>
            <Lbl htmlFor="edit-pass">New PPPoE password</Lbl>
            <Input
               id="edit-pass"
               value={password}
               placeholder="Leave blank to keep the current one"
               onChange={(e) => setPassword(e.target.value)}
            />
            <Hint>
               Changing this disconnects them until their router is given the
               new one.
            </Hint>
         </Field>

         {message ? <Err>{message}</Err> : null}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
            <Primary type="button" disabled={saving} onClick={submit}>
               {saving ? 'Saving…' : 'Save changes'}
            </Primary>
         </Actions>
      </Wrap>
   );
};

const Creds = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 4px 14px;
   margin-bottom: 18px;
`;
const CredRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 10px 0;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const CredKey = styled.span`
   font-size: 13px;
   color: ${colors.GRAY5};
`;
const CredVal = styled.span`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const CopyBtn = styled.button`
   font-family: inherit;
   font-size: 11px;
   font-weight: 700;
   padding: 4px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   background: transparent;
   color: ${colors.PRIMARY};
   cursor: pointer;
`;

const Wrap = styled.div`
   padding: 4px 2px;
`;
const Who = styled.div`
   font-size: 13px;
   color: ${colors.GRAY5};
   margin-bottom: 16px;
`;
const Mono = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   font-weight: 700;
   color: inherit;
`;
const Field = styled.div`
   margin-bottom: 16px;
`;
const Lbl = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 700;
   margin-bottom: 6px;
`;
const base = `
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 14px;
   padding: 11px 12px;
   border-radius: 9px;
   background: transparent;
   color: inherit;
`;
const Select = styled.select`
   ${base}
   border: 1px solid ${colors.GRAY12};
`;
const Input = styled.input`
   ${base}
   border: 1px solid ${colors.GRAY12};
`;
const Num = styled.input`
   ${base}
   border: 1px solid ${colors.GRAY12};
   max-width: 120px;
`;
const Hint = styled.div`
   margin-top: 5px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Warn = styled.div`
   margin-bottom: 16px;
   padding: 11px 13px;
   border-radius: 9px;
   font-size: 13px;
   line-height: 1.55;
   border: 1px solid ${colors.RED};
   color: ${colors.RED};
`;
const Info = styled.div`
   margin-bottom: 16px;
   padding: 11px 13px;
   border-radius: 9px;
   font-size: 13px;
   line-height: 1.55;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY5};
`;
const Err = styled.p`
   margin: 0 0 12px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.RED};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
const Ghost = styled.button`
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 16px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   background: transparent;
   color: inherit;
   cursor: pointer;
`;
const Primary = styled.button`
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 18px;
   border: none;
   border-radius: 9px;
   background: ${colors.PRIMARY};
   color: #fff;
   cursor: pointer;
`;

export default IspAccountEdit;
